import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PermissionSection } from 'shared/permissions';
import { useToast } from '@/providers/Toast.provider';
import usePermissionLabels from '@/hooks/usePermissionLabels';
import useFetchPartnerPartnerPermissions from '@/hooks/useApi/useFetchPartnerPartnerPermissions';
import adaptSubAccountPermissions from '@/components/Entities/Accounts/Account/adaptSubAccountPermissions';

const hasItems = (section: PermissionSection): boolean => section.items.length > 0 || section.sections.some(hasItems);

/**
 * A partner account's payment-rail toggles (`GET /partner/partners/permissions`, read as the parent by
 * its ID), adapted exactly as the account Permissions tab adapts an account's — so the partner page's
 * Permissions tab and the list's Partner Permissions drawer show one tree the same way. Reads when
 * `isEnabled` turns on (the drawer: on open) and keeps the tree when it turns off, so a closing drawer
 * slides out with its rails, not a placeholder; a failure is toasted here, once, for whichever asked.
 */
export default function usePartnerPermissionSections(partnerAccountID: string, isEnabled: boolean) {
  const { t } = useTranslation('translations', { keyPrefix: 'Partners.PartnerManagement.PartnerPermissions' });
  const toast = useToast();
  const { resolvePermissionLabel, resolveSectionLabel } = usePermissionLabels();
  const { partnerPermissions, fetchPartnerPartnerPermissions, reset } = useFetchPartnerPartnerPermissions();
  const [hasFailed, setHasFailed] = useState(false);

  useEffect(() => {
    // The previous partner's rails must not show under the next partner's name.
    reset();
    setHasFailed(false);
  }, [partnerAccountID, reset]);

  useEffect(() => {
    if (!isEnabled) return undefined;

    setHasFailed(false);

    // The hook drops a superseded response itself, but its rejection still reaches this caller — so a
    // request for a partner account the user has since left must not mark this one failed.
    let isCurrentPartner = true;

    fetchPartnerPartnerPermissions(partnerAccountID).catch((error) => {
      if (!isCurrentPartner) return;

      // The fetch hook has logged the cause; the caller shows the rails as unavailable and the toast says why.
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';

      setHasFailed(true);

      toast.neutral(
        t('loadError', { defaultValue: 'Could not load the partner permissions: {{errorMessage}}', errorMessage }),
      );
    });

    return () => {
      isCurrentPartner = false;
    };
    // toast is deliberately left out: the provider recreates its value every render, so depending
    // on it would refetch on every toast.
  }, [isEnabled, partnerAccountID, fetchPartnerPartnerPermissions, t]);

  const sections = useMemo(
    () =>
      adaptSubAccountPermissions(partnerPermissions ?? [], {
        resolveItemLabel: resolvePermissionLabel,
        resolveSectionLabel: (sectionID) => resolveSectionLabel(sectionID),
      }),
    [partnerPermissions, resolvePermissionLabel, resolveSectionLabel],
  );

  // The route always sends Send and Collect; with no rail of the country in either there is nothing to show.
  const hasRails = useMemo(() => sections.some(hasItems), [sections]);

  const message = hasFailed
    ? t('unavailable', { defaultValue: 'The partner permissions are unavailable.' })
    : partnerPermissions && !hasRails
      ? t('empty', { defaultValue: 'This partner account has no permissions to show.' })
      : undefined;

  return {
    sections,
    /** Not yet answered: neither the tree nor a failure is in. */
    isLoading: !partnerPermissions && !hasFailed,
    /** Why there is nothing to list — failed, or no rails — or undefined when the sections are the content. */
    message,
  };
}
